// The Athena conversation: privileged-mode chip, message log, starter
// questions, composer. One component for the side panel and the full
// page (/shell/copilot); state lives in AthenaProvider, so both show
// the same conversation.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Loader2,
  Send,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  User as UserIcon,
  Wrench,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import type { AthenaToolCall } from '../../lib/copilot';
import { TURN_MAX_LEN, useAthena, type AthenaPage, type UiMessage } from './AthenaProvider';

const SUGGESTIONS = [
  // MP252 — Tool-driven questions: Athena calls the posture tools and
  // answers with live numbers.
  'How many devices are non-compliant right now?',
  'Which posture checks are failing on the most devices?',
  'Where are our EDR coverage gaps?',
  'Which policies are currently matching devices?',
  // How-to questions are answered without tools.
  'How do I set up Salesforce Health Check posture?',
  'How do I clone a built-in policy and edit it?',
];

// Asked from the side panel, where Athena knows the page.
const PANEL_SUGGESTIONS = [
  'What should I look at first on this page?',
  'Explain what this page shows',
  'How many devices are non-compliant right now?',
];

export function AthenaChat({
  variant,
  page,
}: {
  variant: 'page' | 'panel';
  /** The page the panel is open on; sent with each question. */
  page?: AthenaPage;
}) {
  const { session } = useAuth();
  const athena = useAthena();
  const {
    isAdmin,
    isSuperadmin,
    persona,
    messages,
    sending,
    phase,
    inflightToolCalls,
    error,
    elevation,
    elevationBusy,
    start,
    send,
    toggleElevation,
  } = athena;
  const name = persona?.name ?? 'Athena';
  const panel = variant === 'panel';
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputId = panel ? 'athena-panel-input' : 'copilot-input';
  const hintId = `${inputId}-hint`;

  useEffect(() => {
    start();
    // MP267 — Focus the composer so typing can start at once.
    inputRef.current?.focus();
  }, [start]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const submit = useCallback(
    async (text: string) => {
      if (!text.trim() || sending) return;
      setInput('');
      await send(text, page);
      inputRef.current?.focus();
    },
    [send, page, sending],
  );

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit(input);
    }
  }

  if (!isAdmin) {
    return (
      <div className={`${panel ? 'm-4' : 'mt-3'} rounded-md border bg-card p-4 text-sm text-muted-foreground`}>
        {name} is available to organization owners and admins. Ask one of them if you need access.
      </div>
    );
  }

  const suggestions = panel ? (page ? PANEL_SUGGESTIONS : SUGGESTIONS.slice(0, 3)) : SUGGESTIONS;

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${panel ? 'px-3 pb-3' : ''}`}>
      {isSuperadmin && (
        // MP279 — Privileged mode. Off by default; enabling grants 30
        // minutes in which destructive tools can run.
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card p-2 text-xs">
          <div className="flex min-w-0 items-center gap-2">
            {elevation.elevated ? (
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            ) : (
              <ShieldOff className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            )}
            <span>
              Privileged mode:{' '}
              <span className={elevation.elevated ? 'font-semibold text-emerald-700' : 'font-medium text-slate-600'}>
                {elevation.elevated ? 'ON' : 'OFF'}
              </span>
              {elevation.elevated && elevation.remainingSeconds !== undefined && (
                <span className="ml-2 text-slate-500">· expires in {formatRemaining(elevation.remainingSeconds)}</span>
              )}
            </span>
            {!elevation.elevated && !panel && (
              <span className="text-slate-500">
                — enable to let {name} run destructive tools for 30 min.
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => void toggleElevation()}
            disabled={elevationBusy}
            title={elevation.elevated ? undefined : `Lets ${name} run destructive tools for 30 minutes`}
            className={
              elevation.elevated
                ? 'inline-flex items-center gap-1 rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50'
                : 'inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50'
            }
          >
            {elevationBusy && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
            {elevation.elevated ? 'Revoke' : panel ? 'Enable' : 'Enable privileged mode'}
          </button>
        </div>
      )}

      <div
        ref={scrollRef}
        // MP267 — role=log + aria-live=polite announces new messages;
        // aria-atomic=false reads only the new turn.
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-label={`Conversation with ${name}`}
        className={`mt-3 flex-1 space-y-4 overflow-y-auto ${panel ? 'px-1' : 'rounded-md border bg-card p-4'}`}
      >
        {messages.map((m) => (
          <ChatBubble key={m.id} message={m} personaName={name} userInitial={initialFor(session?.user)} />
        ))}
        {sending && (
          <div className="space-y-2" aria-live="polite">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span>{phase === 'responding' ? `${name} is writing an answer…` : `${name} is thinking…`}</span>
            </div>
            {/* MP283 — Live tool-use chips; each flips from a spinner
                to a tick or cross when its result arrives. */}
            {inflightToolCalls.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pl-6">
                {inflightToolCalls.map((c, i) => (
                  <span
                    key={`${c.name}-${c.round}-${i}`}
                    className={
                      c.done
                        ? c.isError
                          ? 'inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700'
                          : 'inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700'
                        : 'inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700'
                    }
                  >
                    {c.done ? (
                      c.isError ? (
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <Check className="h-3 w-3" aria-hidden="true" />
                      )
                    ) : (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    )}
                    <Wrench className="h-3 w-3" aria-hidden="true" />
                    <span>{c.name}</span>
                    {c.done && c.durationMs !== undefined && <span className="text-slate-400">· {c.durationMs}ms</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {suggestions.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => void submit(q)}
              className="rounded-full border bg-background px-3 py-1 text-left text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              disabled={sending}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div role="alert" className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit(input);
        }}
        className="mt-3 flex items-end gap-2"
      >
        <label htmlFor={inputId} className="sr-only">
          Message to {name}
        </label>
        <textarea
          id={inputId}
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          maxLength={TURN_MAX_LEN}
          placeholder={panel && page?.title ? `Ask ${name} about ${page.title}…` : `Ask ${name} anything about X-One…`}
          className="min-h-[2.5rem] flex-1 resize-none rounded-md border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          disabled={sending}
          aria-describedby={hintId}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          aria-label={sending ? 'Sending message…' : !input.trim() ? 'Type a message first' : 'Send message'}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" aria-hidden="true" />
          {!panel && 'Send'}
        </button>
      </form>
      <p id={hintId} className="mt-1 text-[11px] text-muted-foreground">
        {name} is AI and can be wrong. Check anything important on the page she points you to.
      </p>
    </div>
  );
}

// MP267 — Custom markdown renderers used inside assistant bubbles.
//  - Links get an external-link affordance + target=_blank + rel=noopener
//    for anything not on the same host / not starting with `/`.
//  - Inline code + code blocks get a subtle background so they stand out
//    against the muted bubble.
//  - Tables get an overflow-x wrapper so wide roll-ups don't blow out
//    the bubble width.
function makeMarkdownComponents(): Components {
  return {
    a({ href, children, ...props }) {
      const isExternal = typeof href === 'string' && /^https?:\/\//i.test(href);
      // A page in the app opens in place, so the chat stays where it is.
      if (typeof href === 'string' && /^\/(?!\/)/.test(href)) {
        return (
          <Link to={href} className="font-medium text-violet-700 underline decoration-violet-400 underline-offset-2 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-100">
            {children}
          </Link>
        );
      }
      return (
        <a
          {...props}
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="inline-flex items-center gap-0.5 font-medium text-violet-700 underline decoration-violet-400 underline-offset-2 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-100"
        >
          {children}
          {isExternal && <ExternalLink className="h-3 w-3" aria-hidden="true" />}
        </a>
      );
    },
    code({ className, children, ...props }) {
      // ReactMarkdown routes inline code AND fenced code blocks through
      // this component. If a language class is present it's a block.
      const isBlock = /language-/.test(className ?? '');
      if (isBlock) {
        return (
          <code
            {...props}
            className={`${className ?? ''} block rounded-md border bg-background/60 p-2 font-mono text-[12px] leading-relaxed`}
          >
            {children}
          </code>
        );
      }
      return (
        <code
          {...props}
          className="rounded bg-background/60 px-1 py-px font-mono text-[11px]"
        >
          {children}
        </code>
      );
    },
    table({ children, ...props }) {
      return (
        <div className="my-2 overflow-x-auto rounded-md border">
          <table {...props} className="w-full text-[12px]">
            {children}
          </table>
        </div>
      );
    },
  };
}

function ChatBubble({
  message,
  personaName,
  userInitial,
}: {
  message: UiMessage;
  personaName: string;
  userInitial: string;
}) {
  const isUser = message.role === 'user';
  const hasTools = !isUser && message.toolCalls && message.toolCalls.length > 0;
  const [copied, setCopied] = useState(false);
  const markdownComponents = useMemo(makeMarkdownComponents, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied (e.g. insecure context) — swallow silently.
    }
  }, [message.content]);

  return (
    <article
      aria-label={`${isUser ? 'You' : personaName} at ${new Date(message.createdAt).toLocaleTimeString()}`}
      className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      {/* Avatar column */}
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
          isUser
            ? 'bg-primary/10 text-primary'
            : 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300'
        }`}
        aria-hidden="true"
      >
        {isUser ? (
          userInitial ? (
            <span>{userInitial}</span>
          ) : (
            <UserIcon className="h-4 w-4" />
          )
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
      </div>

      {/* Bubble column */}
      <div className={`flex min-w-0 max-w-[85%] flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Header row: role name + timestamp + copy button */}
        <div
          className={`mb-0.5 flex items-center gap-2 text-[10px] text-muted-foreground ${
            isUser ? 'flex-row-reverse' : 'flex-row'
          }`}
        >
          <span className="font-semibold uppercase tracking-wide">
            {isUser ? 'You' : personaName}
          </span>
          <time
            dateTime={new Date(message.createdAt).toISOString()}
            title={new Date(message.createdAt).toLocaleString()}
          >
            {formatRelativeTime(message.createdAt)}
          </time>
          {!isUser && (
            <button
              type="button"
              onClick={handleCopy}
              aria-label={copied ? 'Copied' : 'Copy message'}
              className="inline-flex items-center gap-0.5 rounded border bg-background px-1 py-0.5 hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {copied ? (
                <>
                  <Check className="h-2.5 w-2.5" aria-hidden="true" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-2.5 w-2.5" aria-hidden="true" /> Copy
                </>
              )}
            </button>
          )}
        </div>

        <div
          className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm ${
            isUser
              ? 'bg-primary text-primary-foreground'
              : message.refused
                ? 'border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100'
                : 'bg-muted text-foreground'
          }`}
        >
          {message.refused && (
            <div className="mb-1 flex items-center gap-1 text-[11px] font-medium text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              Response withheld
            </div>
          )}

          {hasTools && (
            <div className="mb-2 flex flex-wrap gap-1">
              {message.toolCalls!.map((tc, idx) => (
                <ToolCallChip key={`${tc.name}-${idx}`} call={tc} />
              ))}
            </div>
          )}

          {isUser ? (
            message.content.split('\n').map((line, i) => (
              <p key={i} className={i === 0 ? '' : 'mt-2'}>
                {line}
              </p>
            ))
          ) : (
            <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:mt-3 prose-headings:mb-1 prose-hr:my-3">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

// MP252 + MP267 — Tool-call chip. Click to expand a details panel
// showing full args + tool result. Hover title stays as a fast
// preview for pointer users; the expand click makes the same info
// accessible on touch + via keyboard.
function ToolCallChip({ call }: { call: AthenaToolCall }) {
  const [open, setOpen] = useState(false);
  const argSummary = useMemo(
    () =>
      Object.entries(call.input)
        .map(([k, v]) => `${k}=${JSON.stringify(v).slice(0, 40)}`)
        .join(', '),
    [call.input],
  );
  const tooltip = `${call.name}(${argSummary})\n${call.durationMs}ms\n\n${call.outputPreview}`;
  return (
    <span className="inline-flex flex-col items-start">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        aria-label={`${call.name} tool call — ${open ? 'hide' : 'show'} details`}
        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium focus:outline-none focus:ring-1 focus:ring-ring ${
          call.isError
            ? 'border-destructive/40 bg-destructive/5 text-destructive'
            : 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300'
        }`}
        title={tooltip}
      >
        {open ? (
          <ChevronDown className="h-2.5 w-2.5" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-2.5 w-2.5" aria-hidden="true" />
        )}
        <Wrench className="h-2.5 w-2.5" aria-hidden="true" />
        <span className="font-mono">{call.name}</span>
        <span className="text-muted-foreground">· {call.durationMs}ms</span>
      </button>
      {open && (
        <div className="mt-1 w-full max-w-[520px] rounded border bg-background p-2 text-[10px] shadow-sm">
          <div className="mb-1 text-[9px] uppercase text-muted-foreground">Arguments</div>
          <pre className="mb-2 max-h-32 overflow-auto whitespace-pre-wrap break-all font-mono">
            {JSON.stringify(call.input, null, 2)}
          </pre>
          <div className="mb-1 text-[9px] uppercase text-muted-foreground">Result preview</div>
          <pre
            className={`max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono ${
              call.isError ? 'text-destructive' : ''
            }`}
          >
            {call.outputPreview}
          </pre>
        </div>
      )}
    </span>
  );
}

// MP267 — Helpers.

function formatRelativeTime(ts: number): string {
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(ts).toLocaleDateString();
}

function initialFor(user?: { email?: string | null; displayName?: string | null } | null): string {
  const src = user?.displayName || user?.email;
  if (!src) return '';
  const trimmed = src.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase();
}

// MP279 — Countdown formatter for the sudo chip. "29m 47s" style so
// the operator can eyeball how long they have without doing math.
function formatRemaining(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return `${min}m ${sec.toString().padStart(2, '0')}s`;
}
